/** Small pieces shared by the Types pages and dialogs. */
import { Banner, Button, IconButton, TextField } from '@codex/ui';
import type { ReactNode } from 'react';
import { useId } from 'react';
import type { OptionRow } from './model/options.js';
import './types.css';

/** A refusal in the server's words, as an alert. */
export function RefusalBanner({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <Banner tone="danger" live="assertive" action={action}>
      {children}
    </Banner>
  );
}

/** A native `select` in the kit's field frame. */
export function SelectField({
  label,
  value,
  onChange,
  disabled,
  testId,
  children,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  testId?: string;
  children: ReactNode;
}) {
  const id = useId();
  return (
    <div className="cx-field">
      <label htmlFor={id} className="cx-field__label">
        {label}
      </label>
      <span className="cx-input" data-control-boundary="">
        <select
          id={id}
          className="cx-input__el"
          value={value}
          disabled={disabled}
          data-testid={testId}
          onChange={(event) => onChange(event.currentTarget.value)}
        >
          {children}
        </select>
      </span>
    </div>
  );
}

/** Rows with rename in place, add, move and remove. */
export function OptionsEditor({
  rows,
  onChange,
}: {
  rows: readonly OptionRow[];
  onChange: (rows: OptionRow[]) => void;
}) {
  const move = (index: number, by: -1 | 1) => {
    const next = [...rows];
    const [row] = next.splice(index, 1);
    if (row) next.splice(index + by, 0, row);
    onChange(next);
  };
  const nextId = rows.reduce((top, row) => Math.max(top, row.id + 1), 0);
  return (
    <fieldset className="cx-types__options">
      <legend>Options</legend>
      <ul>
        {rows.map((row, index) => (
          <li key={row.id}>
            <TextField
              label={`Option ${index + 1}`}
              hideLabel
              value={row.text}
              onChange={(event) => {
                const text = event.currentTarget.value;
                onChange(rows.map((r) => (r.id === row.id ? { ...r, text } : r)));
              }}
            />
            <IconButton
              icon="caret-up"
              label={`Move option ${index + 1} up`}
              size="sm"
              disabled={index === 0}
              onClick={() => move(index, -1)}
            />
            <IconButton
              icon="caret-down"
              label={`Move option ${index + 1} down`}
              size="sm"
              disabled={index === rows.length - 1}
              onClick={() => move(index, 1)}
            />
            <IconButton
              icon="x"
              label={`Remove option ${index + 1}`}
              size="sm"
              onClick={() => onChange(rows.filter((r) => r.id !== row.id))}
            />
          </li>
        ))}
      </ul>
      <Button
        size="sm"
        icon="plus"
        onClick={() => onChange([...rows, { id: nextId, original: null, text: '' }])}
      >
        Add option
      </Button>
    </fieldset>
  );
}
